import {
  createParser,
  EventSourceMessage,
  EventSourceParser,
  ParseError
} from 'eventsource-parser';

export class ResponseError extends Error {
  response: Response;
  constructor(response: Response) {
    super('API response error');
    this.response = response;
  }
}

export class TimeoutError extends Error {
  constructor() {
    super('Timeout error');
  }
}

type EventStreamReader = ReadableStreamDefaultReader<Uint8Array>;

const decoder = new TextDecoder();

const streamTimeout = 60000;

async function readStreamWithCustomTimeout(
  reader: EventStreamReader,
  parser: EventSourceParser,
  abort?: AbortSignal
) {
  abort?.addEventListener('abort', () => {
    reader.cancel();
  });

  let done = false;

  while (!done) {
    const timeoutPromise = new Promise<never>((_, reject) => {
      window.setTimeout(() => {
        reject(new TimeoutError());
      }, streamTimeout);
    });

    const { value, done: doneReading } = await Promise.race([reader.read(), timeoutPromise]);

    done = doneReading;
    const chunk = decoder.decode(value);
    parser.feed(chunk);
  }
}

type EventContext = {
  event: EventSourceMessage;
  cancel: () => PromiseLike<void> | void;
};

type OnEventHandler = (context: EventContext) => void;
type OnParserErrorHandler = (error: ParseError) => void;

type CreateEventParserWithEnhancedContextOptions = {
  reader: EventStreamReader;
  onEvent: OnEventHandler;
  onParserError?: OnParserErrorHandler;
};

const createEventParserWithEnhancedContext = ({
  reader,
  onEvent,
  onParserError
}: CreateEventParserWithEnhancedContextOptions) =>
  createParser({
    onEvent: (event: EventSourceMessage) => {
      const context = {
        event,
        cancel: () => reader.cancel()
      };

      onEvent(context);
    },
    onError: onParserError
  });

type ProcessEventStreamOptions = {
  response: Response;
  onEvent: OnEventHandler;
  onParserError?: OnParserErrorHandler;
  abort?: AbortSignal;
};

export const processEventStream = async ({
  response,
  onEvent,
  onParserError,
  abort
}: ProcessEventStreamOptions) => {
  if (!response.ok || !response.body) {
    throw new ResponseError(response);
  }

  const reader = response.body.getReader();
  const parser = createEventParserWithEnhancedContext({ reader, onEvent, onParserError });

  await readStreamWithCustomTimeout(reader, parser, abort);
};
